import * as Yup from "yup";
import { useFormik } from "formik";
import { throttle } from "lodash";
import React, { useMemo, useState } from "react";

import PlaceIcon from "@mui/icons-material/Place";
import { Autocomplete, InputAdornment, TextField } from "@mui/material";

import { usePartnerAxios } from "../../common/partner-axios";
import { QuestionaireBasePage } from "./QuestionaireBasePage";
import { Question, QuestionDefinitionDto } from "./questionaire-types";

type SuggestionLocation = {
  placeId: string;
  description: string;
};

export type LocationAnwser = {
  googlePlaceId: string;
  country: string;
  name: string;
  latitude: string;
  longitude: string;
};

type LocationPageProps = {
  questionDefinition: QuestionDefinitionDto;
  onNext: (question: Question) => void;
  isFrenchLanguage:boolean;
};

export const LocationPage = ({ questionDefinition, onNext,isFrenchLanguage }: LocationPageProps) => {
  const partnerAxios = usePartnerAxios();
  const [suggestionLocations, setSuggestionLocations] = useState<SuggestionLocation[]>([]);
  const formik = useFormik({
    initialValues: {
      location: {} as SuggestionLocation,
    },
    validationSchema: Yup.object().shape({
      location: Yup.object().shape({
        description:!isFrenchLanguage? 
        Yup.string().required("Please enter your location"):
        Yup.string().required("Veuillez entrer votre emplacement"),
        
      }),
    }),
    onSubmit: (value) => {
      const answer = {
        googlePlaceId: value.location.placeId,
        name: value.location.description,
        country: "",
        longitude: "",
        latitude: "",
      };
      const question = {
        key: questionDefinition.key,
        label: questionDefinition.title,
        answers: [answer],
      };
      onNext(question);
    },
  });

  const fetch = useMemo(
    () =>
      throttle(async (inputValue: string) => {
        if (!inputValue) {
          setSuggestionLocations([]);
          return;
        }

        const resp = await partnerAxios.get(`lookup/place/autocomplete/json?input=${inputValue}&types=(regions)`);
        const suggestionLocations = resp.data.predictions.map((l: any) => ({ placeId: l["place_id"], description: l["description"] } as SuggestionLocation));
        setSuggestionLocations(suggestionLocations);
      }, 200),
    [partnerAxios]
  );

  const handleInput = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    fetch(value);
  };

  return (
    <QuestionaireBasePage questionDefinition={questionDefinition} formik={formik} isSaving={false} isFrenchLanguage={isFrenchLanguage}>
      <Autocomplete
        filterOptions={(x) => x}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.placeId}>
              {option.description}
            </li>
          );
        }}
        renderInput={(params) => {
          const customParams = { ...params };
          customParams.InputProps.startAdornment = (
            <InputAdornment position="start">
              <PlaceIcon />
            </InputAdornment>
          );
          return (
            <TextField
              {...customParams}
              name="location.description"
              placeholder="Type in city and country"
              value={formik.values.location.description}
              error={formik.touched.location && Boolean(formik.errors.location?.description)}
              helperText={formik.touched.location ? formik.errors.location?.description : ""}
              size="small"
              sx={{ marginTop: 1 }}
              onInput={handleInput}
            />
          );
        }}
        options={suggestionLocations}
        getOptionLabel={(option) => option.description}
        onChange={(_, value: SuggestionLocation | null) => {
          formik.setFieldValue("location", value);
        }}
      />
    </QuestionaireBasePage>
  );
};
